import { ProductInventoryResponse } from '../../redux-toolkits/products/products.type'
import { FulfilmentCentre } from '../../redux-toolkits/fulfilment-centres/fulfilmentCentres.type'
import { VariantCombination } from '../../redux-toolkits/products/products.type'
export function getEditableInventoryCentres(
  creatorCentres: FulfilmentCentre[],
  assignedCentres: FulfilmentCentre[] = [],
): FulfilmentCentre[] {
  const assignedIds = new Set(assignedCentres.map(({ _id }) => _id))
  return creatorCentres.filter(({ _id }) => assignedIds.has(_id))
}

export type ProductInventoryQuantities = Record<string, number>
export type VariantInventoryQuantities = Record<
  string,
  ProductInventoryQuantities
>

export function getVariantCombinationInventoryKey(
  combination: VariantCombination,
): string {
  if (combination._id) return combination._id

  return combination.variantCombination
    .map(({ variant, value }) => `${variant._id}:${value}`)
    .sort()
    .join('|')
}

export function buildProductInventoryQuantities(
  fulfilmentCentres: FulfilmentCentre[],
  inventory?: ProductInventoryResponse['data'],
): ProductInventoryQuantities {
  const quantities = Object.fromEntries(
    fulfilmentCentres.map(({ _id }) => [_id, 0]),
  )

  for (const centreInventory of inventory?.fulfilmentCentres ?? []) {
    if (centreInventory.fulfilmentCentreId in quantities) {
      quantities[centreInventory.fulfilmentCentreId] = centreInventory.quantity
    }
  }

  return quantities
}

export function buildVariantInventoryQuantities(
  variantCombinations: VariantCombination[],
  fulfilmentCentres: FulfilmentCentre[],
  inventory?: ProductInventoryResponse['data'],
): VariantInventoryQuantities {
  return Object.fromEntries(
    variantCombinations.map((combination) => {
      const combinationKey = getVariantCombinationInventoryKey(combination)
      const quantities = Object.fromEntries(
        fulfilmentCentres.map(({ _id }) => [_id, 0]),
      )

      if (combination._id) {
        for (const centreInventory of inventory?.fulfilmentCentres ?? []) {
          if (!(centreInventory.fulfilmentCentreId in quantities)) continue
          const variantInventory = centreInventory.variants.find(
            ({ variantCombinationId }) =>
              variantCombinationId === combination._id,
          )
          if (variantInventory) {
            quantities[centreInventory.fulfilmentCentreId] =
              variantInventory.quantity
          }
        }
      }

      return [combinationKey, quantities]
    }),
  )
}

export function sumInventoryQuantities(
  quantities: ProductInventoryQuantities,
): number {
  return Object.values(quantities).reduce(
    (total, quantity) => total + Number(quantity || 0),
    0,
  )
}

export function sumVariantInventoryQuantities(
  quantities: VariantInventoryQuantities,
  variantCombinations?: VariantCombination[],
): number {
  const selectedQuantities = variantCombinations
    ? variantCombinations.map(
        (combination) =>
          quantities[getVariantCombinationInventoryKey(combination)] ?? {},
      )
    : Object.values(quantities)

  return selectedQuantities.reduce(
    (total, combinationQuantities) =>
      total + sumInventoryQuantities(combinationQuantities),
    0,
  )
}
